class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Ignore data-error attribute changes (error state means we should stop trying)
      if (mutation.attributeName === 'data-error') continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#initCarouselFooters();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, error } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If an error has already occurred, stop trying
    if (error) {
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#initCarouselFooters();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Initializes the progress bar and arrows for every resource-list carousel footer in the section.
   */
  #initCarouselFooters() {
    this.querySelectorAll('[data-carousel-footer]').forEach((footer) => this.#initCarouselFooter(footer));
  }

  /**
   * Wires a single carousel footer (progress bar + arrows) to its slideshow's scroll position.
   * @param {Element} footer
   */
  #initCarouselFooter(footer) {
    if (!(footer instanceof HTMLElement) || footer.dataset.carouselFooterReady === 'true') return;

    const slideshow = footer.closest('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');
    const progress = footer.querySelector('[data-carousel-progress]');
    const progressRail = footer.querySelector('.bz-product-recommendation__progress');
    const mobileFooter = slideshow?.querySelector('[data-carousel-mobile-footer]');
    const mobileRail = mobileFooter?.querySelector('.bz-product-recommendation__mobile-progress');
    const mobileProgress = mobileFooter?.querySelector('[data-carousel-mobile-progress]');

    if (!slideshow || !scroller || !progress || !progressRail) return;

    footer.dataset.carouselFooterReady = 'true';

    this.#disableMouseDrag(scroller);

    const previousButton = footer.querySelector('[data-carousel-action="previous"]');
    const nextButton = footer.querySelector('[data-carousel-action="next"]');

    const getCards = () => Array.from(scroller.children);

    /** @param {Element[]} cards */
    const getVisibleCards = (cards) => {
      if (!cards.length) return 1;

      const cardWidth = cards[0].getBoundingClientRect().width;
      const scrollerStyles = window.getComputedStyle(scroller);
      const gap = parseFloat(scrollerStyles.columnGap || scrollerStyles.gap || '0') || 0;

      return cardWidth ? Math.max(1, Math.floor((scroller.clientWidth + gap) / (cardWidth + gap))) : 1;
    };

    /**
     * Scroll position that puts each card where the first card sits at rest.
     * Measured relative to the first card, so track padding or gutters don't skew it.
     * @param {Element[]} cards
     */
    const getCardStops = (cards) => {
      const firstLeft = cards[0]?.getBoundingClientRect().left ?? 0;
      return cards.map((card) => card.getBoundingClientRect().left - firstLeft);
    };

    /**
     * Index of the card whose stop is closest to the current scroll position.
     * @param {Element[]} cards
     */
    const getCurrentIndex = (cards) => {
      const scrollLeft = Math.abs(scroller.scrollLeft);
      let current = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      getCardStops(cards).forEach((stop, index) => {
        const distance = Math.abs(stop - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          current = index;
        }
      });

      return current;
    };

    /**
     * Scrolls exactly to the card at `index`.
     * @param {Element[]} cards
     * @param {number} index
     */
    const selectCard = (cards, index) => {
      const stops = getCardStops(cards);
      const target = stops[Math.max(0, Math.min(index, stops.length - 1))];
      if (target == null) return;

      scroller.scrollTo({ left: target, behavior: 'smooth' });
    };

    footer.addEventListener('click', (event) => {
      const button =
        event.target instanceof Element ? event.target.closest('[data-carousel-action]') : null;
      if (!button || !footer.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const cards = getCards();
      const maxIndex = Math.max(cards.length - getVisibleCards(cards), 0);
      const currentIndex = Math.min(getCurrentIndex(cards), maxIndex);
      const direction = button.getAttribute('data-carousel-action');
      const nextIndex = direction === 'previous' ? currentIndex - 1 : currentIndex + 1;

      selectCard(cards, Math.max(0, Math.min(nextIndex, maxIndex)));
    });

    const update = () => {
      const cards = getCards();
      if (!cards.length) return;

      const visibleCards = getVisibleCards(cards);
      const maxIndex = Math.max(cards.length - visibleCards, 0);
      const currentIndex = Math.min(getCurrentIndex(cards), maxIndex);
      const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const currentScroll = Math.min(Math.abs(scroller.scrollLeft), maxScroll);

      // Scrollbar-style thumb: width reflects the visible share of cards, position follows scroll.
      const progressWidth = Math.min((visibleCards / cards.length) * 100, 100);
      progress.style.width = `${progressWidth}%`;

      const progressTravel = Math.max(progressRail.clientWidth - progress.offsetWidth, 0);
      const progressPosition = maxScroll > 0 ? (currentScroll / maxScroll) * progressTravel : 0;

      progress.style.left = `${progressPosition}px`;
      footer.classList.toggle('is-static', maxScroll <= 1);

      if (mobileFooter && mobileRail && mobileProgress) {
        const mobileTravel = Math.max(mobileRail.clientWidth - mobileProgress.offsetWidth, 0);
        const mobilePosition = maxScroll > 0 ? (currentScroll / maxScroll) * mobileTravel : 0;

        mobileFooter.hidden = maxScroll <= 1;
        mobileProgress.style.left = `${mobilePosition}px`;
      }

      if (previousButton instanceof HTMLButtonElement) {
        previousButton.disabled = currentIndex <= 0;
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = currentIndex >= maxIndex;
      }
    };

    let scrollFrame = 0;
    scroller.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) return;

        scrollFrame = window.requestAnimationFrame(() => {
          update();
          scrollFrame = 0;
        });
      },
      { passive: true }
    );

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(update, 120);
    });

    update();
  }

  /**
   * Turns off mouse drag-to-scroll on the recommendation cards. The slideshow component starts its
   * drag on `mousedown`, so the event is stopped in the capture phase before it reaches the slideshow
   * or the nested card galleries. Touch swipe and trackpad scrolling are native and keep working.
   * @param {HTMLElement} scroller
   */
  #disableMouseDrag(scroller) {
    scroller.addEventListener('mousedown', (event) => event.stopPropagation(), { capture: true });
    scroller.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
    // Disconnect observers to prevent infinite retry loops
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
